import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Globe,
  Bell,
  LogOut,
  Menu,
  X,
  Home,
  FileText,
  Calendar,
  Video,
  Users,
  Activity,
  Pill,
} from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { logout } from '@/lib/auth-actions';
import { useLocation } from 'wouter';

export default function TopNavigation() {
  const { language, toggleLanguage, t: _t, isRTL } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigationItems = [
    {
      href: '/',
      label: isRTL ? 'الرئيسية' : 'Dashboard',
      icon: Home,
      active: location === '/',
    },
    {
      href: '/telemedicine',
      label: isRTL ? 'استشارة مرئية' : 'Telemedicine',
      icon: Video,
      active: location === '/telemedicine',
    },
    {
      href: '/appointments',
      label: isRTL ? 'المواعيد' : 'Appointments',
      icon: Calendar,
      active: location === '/appointments',
    },
    {
      href: '/records',
      label: isRTL ? 'السجلات الطبية' : 'Medical Records',
      icon: FileText,
      active: location === '/records',
    },
    {
      href: '/medications',
      label: isRTL ? 'الأدوية' : 'Medications',
      icon: Pill,
      active: location === '/medications',
    },
    {
      href: '/health-metrics',
      label: isRTL ? 'المؤشرات الصحية' : 'Health Metrics',
      icon: Activity,
      active: location === '/health-metrics',
    },
  ];

  const handleLogout = () => {
    void logout();
  };

  return (
    <nav
      aria-label={isRTL ? 'التنقل الرئيسي' : 'Main navigation'}
      className={`border-medical-gray sticky top-0 z-50 border-b bg-white shadow-sm ${isRTL ? 'rtl' : 'ltr'}`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <div className="flex flex-shrink-0 items-center">
              <div
                className="bg-medical-primary flex h-8 w-8 items-center justify-center rounded-lg"
                aria-hidden="true"
              >
                <Users className="h-5 w-5 text-white" />
              </div>
              <span className="text-medical-dark ml-2 text-xl font-bold">
                {isRTL ? 'طبيب' : 'Tabib'}
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="ml-8 hidden md:block">
              <div className="flex items-center space-x-4">
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      aria-current={item.active ? 'page' : undefined}
                      className={`focus-visible:ring-primary flex items-center space-x-2 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${
                        item.active
                          ? 'bg-medical-primary text-white'
                          : 'hover:bg-medical-gray hover:text-medical-primary text-gray-600'
                      } ${isRTL ? 'space-x-reverse' : ''}`}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      <span>{item.label}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right side actions */}
          <div className="hidden items-center space-x-4 md:flex">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="hover:text-medical-primary text-gray-600"
            >
              <Globe className="mr-2 h-4 w-4" aria-hidden="true" />
              {language === 'en' ? 'العربية' : 'English'}
            </Button>

            {/* Notifications */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                className="hover:text-medical-primary text-gray-600"
                aria-label={isRTL ? 'الإشعارات' : 'Notifications'}
              >
                <Bell className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>

            {/* User Menu */}
            {isAuthenticated && (
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">
                  {user?.firstName || (isRTL ? 'المستخدم' : 'User')}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-red-600 hover:bg-red-50"
                >
                  <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                  {isRTL ? 'خروج' : 'Logout'}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-label={
                mobileMenuOpen
                  ? isRTL
                    ? 'إغلاق القائمة'
                    : 'Close menu'
                  : isRTL
                    ? 'فتح القائمة'
                    : 'Open menu'
              }
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Menu className="h-5 w-5" aria-hidden="true" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden" role="menu">
            <div className="border-medical-gray space-y-1 border-t bg-white px-2 pt-2 pb-3 sm:px-3">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    aria-current={item.active ? 'page' : undefined}
                    role="menuitem"
                    className={`focus-visible:ring-primary block flex items-center space-x-3 rounded-md px-3 py-2 text-base font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${
                      item.active
                        ? 'bg-medical-primary text-white'
                        : 'hover:bg-medical-gray hover:text-medical-primary text-gray-600'
                    } ${isRTL ? 'space-x-reverse' : ''}`}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                    <span>{item.label}</span>
                  </a>
                );
              })}

              {/* Mobile Language Toggle */}
              <div className="px-3 py-2">
                <Button
                  variant="ghost"
                  onClick={toggleLanguage}
                  className="w-full justify-start text-gray-600"
                >
                  <Globe className="mr-3 h-5 w-5" aria-hidden="true" />
                  {language === 'en' ? 'العربية' : 'English'}
                </Button>
              </div>

              {/* Mobile User Actions */}
              {isAuthenticated && (
                <div className="border-medical-gray border-t px-3 py-2">
                  <div className="mb-2 text-sm font-medium text-gray-900">
                    {user?.firstName || (isRTL ? 'المستخدم' : 'User')}
                  </div>
                  <Button
                    variant="ghost"
                    onClick={handleLogout}
                    className="w-full justify-start text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="mr-3 h-5 w-5" aria-hidden="true" />
                    {isRTL ? 'تسجيل الخروج' : 'Sign Out'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
